import { useStore } from '@/store/store';
import { useEffect, useRef } from 'react';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getServerTime } from '@/logic/aggregation/server-time';
import { CLEAR_SESSION_REASON, useAccountUm } from '@/hooks/use-account-um';

export type UseSessionTimerProps = {
    sessionStartTime: string | null;
};

export const TIMERS = {
    serverTimeMark: 'serverTimeMark',
    currentTimeMeasure: 'currentTimeMeasure',
    sessionTimeMeasure: 'sessionTimeMeasure'
};

export const useSessionTimers = ({ sessionStartTime }: UseSessionTimerProps) => {
    const { sessionTime, setSessionTime } = useStore((state) => state.timer);
    const { isAndroidWrapper, isIosWrapper } = useGlobalContext();
    const isNativeApp = isAndroidWrapper || isIosWrapper;
    const { isSessionValid, triggerLogOut, getSession } = useAccountUm();
    const sessionStartMsRef = useRef<number>(0);
    const sessionExpiryMsRef = useRef<number>(0);

    // Store the initial performance time and offset to calculate session time
    // without depending on system clock changes
    const initialPerformanceTimeRef = useRef<number | null>(null);
    const sessionStartOffsetRef = useRef<number | null>(null);

    const msToTime = (ms: number) => {
        const totalSeconds = Math.floor(ms / 1000);

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        // pad with leading zeros if needed
        const h = String(hours).padStart(2, '0');
        const m = String(minutes).padStart(2, '0');
        const s = String(seconds).padStart(2, '0');

        return `${h}:${m}:${s}`;
    };

    const updateSessionTimer = () => {
        if (!sessionStartTime || !window?.performance) {
            return;
        }

        // Initialize offset on first call if not already set
        if (initialPerformanceTimeRef.current === null || sessionStartOffsetRef.current === null) {
            const sessionStartTimestamp = new Date(sessionStartTime).valueOf();

            const mountSystemTime = Date.now();
            initialPerformanceTimeRef.current = window.performance.now();
            // Calculate offset: how much time passed between session start and mount
            sessionStartOffsetRef.current = mountSystemTime - sessionStartTimestamp;
        }

        if (window.performance.getEntriesByName(TIMERS.serverTimeMark).length <= 0) {
            window.performance.mark(TIMERS.serverTimeMark);
        }

        window.performance.clearMeasures(TIMERS.sessionTimeMeasure);
        window.performance.measure(TIMERS.sessionTimeMeasure, TIMERS.serverTimeMark);

        // Calculate elapsed time using performance.now() (monotonic, not affected by system clock)
        // elapsed = (current performance time - initial performance time) + offset
        const elapsedPerformanceTime = window.performance.now() - initialPerformanceTimeRef.current;
        const time = elapsedPerformanceTime + sessionStartOffsetRef.current;

        if (Number.isNaN(time) || time < 0) {
            return;
        }

        setSessionTime(msToTime(Math.floor(time)));
    };

    useEffect(() => {
        if (!sessionStartTime) return () => {};
        const interval = setInterval(updateSessionTimer, 1000);

        const onVisibilityChange = async () => {
            if (!sessionStartTime || !window.performance) return;

            if (document.visibilityState !== 'visible') {
                getSession().then((session) => {
                    if (session) {
                        const sessionStartMs = new Date(sessionStartTime).valueOf();
                        sessionExpiryMsRef.current = session.expires.getTime();
                        sessionStartMsRef.current = sessionStartMs;
                    }
                });
            } else {
                const sessionValid = await isSessionValid();
                getServerTime('UTC')
                    .then(async (serverTimeStr: string) => {
                        const serverNow = new Date(serverTimeStr).getTime();

                        // check if session is valid and if it had expired while the app was not visible
                        // if so, set the session time to the duration of the session and trigger log out
                        if (!sessionValid && sessionExpiryMsRef.current && serverNow - sessionExpiryMsRef.current > 0) {
                            const sessionDuration = msToTime(
                                Math.floor(sessionExpiryMsRef.current - sessionStartMsRef.current)
                            );
                            setSessionTime(sessionDuration);
                            if (isNativeApp) await triggerLogOut(CLEAR_SESSION_REASON.EXPIRY_TIMER);
                        } else {
                            if (Number.isNaN(serverNow)) return;
                            const sessionStartMs = new Date(sessionStartTime).valueOf();
                            if (Number.isNaN(sessionStartMs)) return;
                            const elapsed = serverNow - sessionStartMs;
                            if (Number.isNaN(elapsed) || elapsed < 0) return;
                            setSessionTime(msToTime(Math.floor(elapsed)));
                            initialPerformanceTimeRef.current = window.performance.now();
                            sessionStartOffsetRef.current = elapsed;
                        }
                    })
                    .catch(() => undefined);
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            getServerTime('UTC')
                .then(async (serverTimeStr: string) => {
                    const serverNow = new Date(serverTimeStr).getTime();
                    if (sessionExpiryMsRef.current && serverNow - sessionExpiryMsRef.current > 0) {
                        const sessionDuration = msToTime(
                            Math.floor(sessionExpiryMsRef.current - sessionStartMsRef.current)
                        );
                        setSessionTime(sessionDuration);
                    }
                })
                .catch(() => undefined);

            document.removeEventListener('visibilitychange', onVisibilityChange);
            clearInterval(interval);
        };
    }, [sessionStartTime, setSessionTime]); // eslint-disable-line react-hooks/exhaustive-deps

    return { sessionTime };
};
